/** GFM pipe-table markdown transformer.
 *
 *  Imports a standard markdown table like::
 *
 *      | Therapy | Duration | Reason for Failure |
 *      | --- | --- | --- |
 *      | Metformin | ~14 months | A1c plateaued at 8.7% |
 *
 *  into a Lexical TableNode tree, and exports the same shape on
 *  serialization. The first row is always treated as the header.
 *
 *  Cell contents are currently imported as plain text — markdown
 *  emphasis markers (``**bold**``, ``*italic*``) survive the roundtrip
 *  but render as literal characters in the editor. Promoting them to
 *  inline format nodes is a deliberate follow-up; the structural
 *  support (rows / columns / headers) is what most LMN tables need
 *  for now.
 */

import type { MultilineElementTransformer } from '@lexical/markdown';
import {
  $createTableCellNode,
  $createTableNodeWithDimensions,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import { $createParagraphNode, $createTextNode, $isElementNode, type LexicalNode } from 'lexical';

/** Matches a candidate table line: starts with ``|`` and has at least
 *  one cell delimiter inside. The followup separator-row check is what
 *  actually qualifies it as a table. */
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;

/** Matches the alignment row that separates header from body. */
const TABLE_SEPARATOR_RE = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;

export const TABLE_TRANSFORMER: MultilineElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  regExpStart: TABLE_ROW_RE,
  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex }) => {
    const headerLine = lines[startLineIndex];
    const separatorLine = lines[startLineIndex + 1];
    if (!headerLine || !separatorLine) return null;
    if (!TABLE_SEPARATOR_RE.test(separatorLine)) return null;

    const headerCells = parsePipeRow(headerLine);
    if (headerCells.length === 0) return null;

    const bodyRows: string[][] = [];
    let lastIdx = startLineIndex + 1;
    for (let i = startLineIndex + 2; i < lines.length; i++) {
      const line = lines[i];
      if (line === undefined) break;
      if (!TABLE_ROW_RE.test(line) || TABLE_SEPARATOR_RE.test(line)) break;
      const cells = parsePipeRow(line);
      if (cells.length === 0) break;
      bodyRows.push(cells);
      lastIdx = i;
    }

    const tableNode = $createTableNodeWithDimensions(bodyRows.length + 1, headerCells.length, {
      rows: false,
      columns: false,
    });

    const rows = tableNode.getChildren();
    const headerRow = rows[0];
    if (headerRow) populateRow(headerRow, headerCells, true);
    for (let r = 0; r < bodyRows.length; r++) {
      const rowNode = rows[r + 1];
      const cellTexts = bodyRows[r];
      if (!rowNode || !cellTexts) break;
      // Cap body-row width to the header so a malformed line with
      // extra ``|`` separators (LLM hiccups, hand-edits) doesn't
      // build a jagged ``TableRowNode`` that neither renders nor
      // round-trips through ``cellToMarkdown``. Trailing cells are
      // dropped intentionally.
      populateRow(rowNode, cellTexts, false, headerCells.length);
    }

    rootNode.append(tableNode);
    return [true, lastIdx];
  },
  export: (node) => {
    if (!$isTableNode(node)) return null;
    const rows = node.getChildren();
    if (rows.length === 0) return null;

    const lines: string[] = [];
    let headerColumnCount = 0;
    rows.forEach((row, rowIndex) => {
      if (!$isTableRowNode(row)) return;
      const cells = row.getChildren().filter($isTableCellNode);
      if (cells.length === 0) return;
      if (rowIndex === 0) headerColumnCount = cells.length;
      const cellText = cells.map(cellToMarkdown);
      lines.push(`| ${cellText.join(' | ')} |`);
      if (rowIndex === 0) {
        const sep = new Array(headerColumnCount).fill('---').join(' | ');
        lines.push(`| ${sep} |`);
      }
    });
    return lines.join('\n');
  },
  regExpEnd: { optional: true, regExp: /^\s*$/ },
  replace: () => false,
  type: 'multiline-element',
};

/** Split a ``| a | b | c |`` line into ``['a', 'b', 'c']``. Strips the
 *  outer pipes if present and honors escaped pipes (``\|``). */
function parsePipeRow(line: string): string[] {
  const trimmed = line.trim();
  // Strip leading/trailing pipes if present.
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  // Split on unescaped pipes.
  const cells: string[] = [];
  let buf = '';
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (ch === '\\' && inner[i + 1] === '|') {
      buf += '|';
      i++;
      continue;
    }
    if (ch === '|') {
      cells.push(buf.trim());
      buf = '';
      continue;
    }
    buf += ch;
  }
  cells.push(buf.trim());
  return cells;
}

function populateRow(
  row: LexicalNode,
  cellTexts: string[],
  isHeader: boolean,
  columnCount?: number
): void {
  if (!$isElementNode(row) || !$isTableRowNode(row)) return;
  // Replace the dimension-defaulted empty cells with fresh ones bearing
  // the right header state and our text. This sidesteps having to mutate
  // existing cell text nodes.
  row.getChildren().forEach((child) => child.remove());
  const cells = columnCount !== undefined ? cellTexts.slice(0, columnCount) : cellTexts;
  for (const text of cells) {
    const cell = $createTableCellNode(
      isHeader ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS
    );
    const paragraph = $createParagraphNode();
    paragraph.append($createTextNode(text));
    cell.append(paragraph);
    row.append(cell);
  }
}

function cellToMarkdown(cell: TableCellNode): string {
  const text = cell.getTextContent();
  // Escape pipes and collapse newlines so the row stays single-line.
  return text.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ').trim();
}
